import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { v4 as uuidv4 } from "uuid";
import FormElement from "./FormElement";
import PropertyEditor from "./PropertyEditor";
import {
  Grid,
  Button,
  Paper,
  Box,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import DraggableElement from "./DraggableElement";
import SortableElement from "./SortableElement";
import defaultElementTypes, { elementTypesForTemplate } from "./elementTypes";
import Clonedeep from "lodash.clonedeep";
import "../../App.css";
import {
  DndContext,
  rectIntersection,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  findElementIndex,
  findElementById,
  removeElement,
  findRootElement,
} from "./utils";
import { MOVEELEMENT } from "../../utils/commonConstant";
const defaultCoordinates = {
  x: 0,
  y: 0,
};

console.log("elementType");
console.log(defaultElementTypes);

const FormBuilder = ({
  elements,
  setElements,
  submitForm,
  isCollapsed = false,
}) => {
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [elementTypes, setElementTypes] = useState(defaultElementTypes);
  const [dragType, setDragType] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [shouldSubmit, setShouldSubmit] = useState(false);

  const [{ translate }, setTranslate] = useState({
    initialTranslate: defaultCoordinates,
    translate: defaultCoordinates,
  });
  // const mainContentRef = useRef(null);
  const [initialWindowScroll, setInitialWindowScroll] =
    useState(defaultCoordinates);
  // const [templateLibraryElenentType, setTemplateLibraryElenentType] = useState(
  //   []
  // );

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  useEffect(() => {
    console.log("ELEMENT UPDATED FROM FORMBUILDER", elements);
  }, [elements, setElements]);
  useEffect(() => {
    if (shouldSubmit) {
      submitForm(); // Will use the latest "elements" state
      setShouldSubmit(false); // Reset the flag
    }
  }, [elements, shouldSubmit, submitForm]);

  const updateElementProps = useCallback(
    (id, newProps) => {
      const updateProps = (elements) => {
        return elements
          .map((element) => {
            if (element.id === id) {
              if (newProps.remove === true) {
                return null; // Mark element for removal
              }
              return { ...element, props: { ...element.props, ...newProps } };
            }
            if (element.items) {
              const updatedItems = updateProps(element.items).filter(
                (item) => item !== null
              );
              return { ...element, items: updatedItems };
            }
            return element;
          })
          .filter((element) => element !== null); // Remove marked elements
      };
      setElements((prevElements) => updateProps(prevElements));

      if (newProps.forceSave === true) {
        setShouldSubmit(true);
      }
    },
    [setElements, submitForm]
  );

  const handleDragStart = ({ active }) => {
    console.log(active);
    if (active.data.current.type !== "draggable") {
      console.log(active.id);
      setActiveId(active.id);
      setDragType("sortable");
    } else {
      setDragType("draggable");
    }

    setInitialWindowScroll({
      x: window.scrollX,
      y: window.scrollY,
    });
  };

  const handleDragMove = ({ delta }) => {
    setTranslate(({ initialTranslate }) => ({
      initialTranslate,
      translate: {
        x: initialTranslate.x + delta.x - initialWindowScroll.x,
        y: initialTranslate.y + delta.y - initialWindowScroll.y,
      },
    }));
  };

  const handleDragEnd = (event) => {
    if (dragType === "draggable") {
      const id = uuidv4();
      const activeId = event.active?.id;

      // Handle the case when there is no element in the canvas and this is
      // the first element.
      // In this case handlDragOver is not invoked.
      if (elements.length === 0) {
        const element = elementTypes.find((element) => element.id === activeId);
        elements.push({
          id: activeId,
          ...element,
        });
      }

      const elementTypesCopy = Clonedeep(elementTypes);
      elementTypesCopy.find((element) => element.id === activeId).id = id;
      setElementTypes(elementTypesCopy);
    }
    setDragId(null);
    setActiveId(null);
    setDragType(null);

    setTranslate(({ translate }) => {
      return {
        translate,
        initialTranslate: translate,
      };
    });
    setInitialWindowScroll(defaultCoordinates);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setDragType(null);
  };

  const handleDragOver = ({ active, over }) => {
    if (!over || !active) {
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    // If source == target for drag and drop or target drop is invalid,
    // don't proceed.
    if (activeId === overId || overId == null) return;

    const activeIndex = findElementIndex(elements, activeId);
    const overIndex = findElementIndex(elements, overId);
    // console.log(activeIndex,'activeIndex')
    // console.log(overIndex,'overIndex')

    if (activeIndex === -1) {
      // If the element is new and being added to the container
      setElements((prevElements) => {
        const element = elementTypes.find((element) => element.id === activeId);

        const elementIndex = findElementIndex(prevElements, overId);
        const targetContainer = findRootElement(prevElements, overId);
        const newElement = {
          id: activeId,
          ...element,
        };

        const newElements = [...prevElements];
        if (
          newElement?.type === "horizontalContainer" &&
          targetContainer?.type === "horizontalContainer"
        ) {
          // Do nothing.
        } else if (targetContainer?.type === "horizontalContainer") {
          // If the target is a horizontal container, add the new element to its items
          if (targetContainer.items) {
            // Find the position within the elements here
            // And insert at the exact position.
            targetContainer.items.splice(elementIndex, 0, newElement);
          } else {
            targetContainer.items = [newElement];
          }
        } else {
          // Add new element to the root level
          // if((overIndex === 0 && newElements.length === 1)) {
          //   newElements.push(newElement);
          // }
          if (overIndex === -1 || overIndex >= newElements.length) {
            newElements.push(newElement);
          } else {
            newElements.splice(overIndex, 0, newElement);
          }
        }
        return newElements;
      });
    } else {
      // If the element is being moved within or between containers
      setElements((prevElements) => {
        let newElements = [...prevElements];
        const targetContainer = findRootElement(prevElements, overId);
        const activeElement = findElementById(prevElements, activeId);

        if (
          activeElement.type === "horizontalContainer" &&
          targetContainer.type === "horizontalContainer"
        ) {
          // Do nothing
        } else {
          // Remove the element from its current position
          const movedElement = removeElement(newElements, activeId);

          if (movedElement) {
            const overElementIndex = findElementIndex(newElements, overId);

            if (targetContainer?.type === "horizontalContainer") {
              // If moving to a horizontal container, add it to the items of the target container
              if (targetContainer) {
                if (targetContainer.items) {
                  targetContainer.items.splice(
                    overElementIndex,
                    0,
                    movedElement
                  );
                } else {
                  targetContainer.items = [movedElement];
                }
              }
            } else {
              // Move the element to the new position in the root level
              if (overIndex === -1 || overIndex >= newElements.length) {
                newElements.push(movedElement);
              } else {
                newElements.splice(overIndex, 0, movedElement);
              }
            }
          }
        }

        return newElements;
      });
    }
  };

  const getDragLayer = () => {
    if (dragType === null) {
      return null;
    }

    return (
      <>
        {dragType === "draggable" && (
          <Box
            style={{
              backgroundColor: "#1976d2",
              width: "200px",
              height: "30px",
              color: "white",
              padding: "10px",
              borderRadius: "8px",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "Arial, sans-serif",
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            DRAG AND DROP TO ADD
          </Box>
        )}
      </>
    );
  };

  const moveElement = (id, direction) => {
    setElements((prevElements) => {
      const index = findElementIndex(prevElements, id);
      const newElements = [...prevElements];

      if (direction === MOVEELEMENT.UP && index > 0) {
        // Move up
        const [removed] = newElements.splice(index, 1);
        newElements.splice(index - 1, 0, removed);
      } else if (
        direction === MOVEELEMENT.DOWN &&
        index < newElements.length - 1
      ) {
        // Move down
        const [removed] = newElements.splice(index, 1);
        newElements.splice(index + 1, 0, removed);
      }

      return newElements;
    });
  };
  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <Grid container spacing={2}>
          {/* Collapsible Section */}
          {!isCollapsed && (
            <Grid item xs={12} sm={6} md={3} className="non-fullscreen">
              <Paper
                elevation={3}
                sx={{
                  padding: 2,
                  height: "auto",
                  position: { xs: "static", md: "sticky" },
                  top: { xs: "auto", md: "160px" },
                }}
              >
                <Box sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  position: "sticky",
                  position: { xs: "static", md: "sticky" },
                  top: { xs: "auto", md: "150px" },
                }}>
                  {elementTypes.map((element, index) => (
                    <DraggableElement
                      id={element.id}
                      dragId={element.id}
                      setDragging={setDragging}
                      translate={translate}
                      key={element.id}
                    >
                      <Button
                        variant="outlined"
                        key={index}
                        style={{ width: "100%" }}
                      >
                        Add {element.type}
                      </Button>
                    </DraggableElement>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* FormElement Section */}
          <Grid
            item
            xs={12}
            sm={isCollapsed ? 12 : 12}
            md={isCollapsed ? 9 : 6} // Adjust width based on isCollapsed
            className="non-fullscreen"
          >
            {/* <Paper
              elevation={3}
              sx={{
                padding: 2,
                minHeight: "700px",
                position: { xs: "static", md: "sticky" },
                top: { xs: "auto", md: 16 },
              }}
            > */}
            <Paper elevation={3}
              sx={{
                width: { xs: "auto", sm:"auto", md: "210mm" , lg: "210mm" },// A4 width
                minHeight: "700px",
                margin: 'auto', // Center the paper on the page if desired
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                padding: '12.7mm', // Add padding inside the paper
                borderRadius: 0
              }}
            >
              <Grid container spacing={2}>
                <SortableContext
                  items={elements}
                  strategy={verticalListSortingStrategy}
                >
                  {elements.map((element, index) => (
                    <Grid
                      xs={12}
                      item
                      key={element.id}
                      sx={{
                        ...(element.type === "logo" && {
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }),
                      }}
                    >
                      <SortableElement
                        setSelectedElementId={setSelectedElementId}
                        handle={true}
                        key={element.id}
                        id={element.id}
                        index={index}
                        element={element}
                        moveElement={moveElement}
                        totalElements={elements.length}
                        isInsideHorizontalContainer={false}
                      >
                        <FormElement
                          element={element}
                          setSelectedElementId={setSelectedElementId}
                          border={true}
                          onPropsChange={(newProps, forceSave = false) =>
                            updateElementProps(element.id, newProps)
                          }
                          selectedElementId={selectedElementId}
                        />
                      </SortableElement>
                    </Grid>
                  ))}
                </SortableContext>
              </Grid>
            </Paper>
          </Grid>

          {/* Property Editor Section */}
          <Grid
            item
            xs={12}
            sm={6}
            md={isCollapsed ? 3 : 3} // Adjust width if needed
          >
            <Paper
              elevation={3}
              sx={{
                padding: 2,
                height: "100%",
                position: { xs: "static", md: "sticky" },
                top: { xs: "auto", md: "150px" },
              }}
            >
              <Box sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                position: { xs: "static", md: "sticky" },
                top: { xs: "auto", md: "150px" },
                left: 0,
              }}>
                {selectedElementId && (
                  <PropertyEditor
                    elements={elements}
                    elementId={selectedElementId}
                    elementType={
                      findElementById(elements, selectedElementId)?.type
                    }
                    elementProps={
                      findElementById(elements, selectedElementId)?.props
                    }
                    onPropsChange={(newProps) =>
                      updateElementProps(selectedElementId, newProps)
                    }
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
        {createPortal(
          <DragOverlay>{getDragLayer()}</DragOverlay>,
          document.body
        )}
      </DndContext>
    </>
  );
};

export default FormBuilder;
